import React, { useState } from "react";
import {
  Button,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import useAwardSearch from "./useAwardSearch";
import DebouncedField from "./DebouncedField";
import FacetSelect, { ACADEMY_OPTIONS, YEAR_OPTIONS } from "./FacetSelect";
import AdvancedFilters from "./AdvancedFilters";
import { useThemeMode } from "../../theme/ThemeContext";
import UTA_MAVERICK from "../../assets/images/uta_mav_logo.png";

const fmt = (n) => (n == null ? "" : Number(n).toLocaleString());

// The persistent search bar that sits above the results/detail Outlet. One
// merged free-text field (awards by name, people by first/last name) plus an
// Awards|People toggle that carries the count for each. The Honorific chip and
// the Advanced facet panel apply to the Awards tab only.
export default function SearchShell({ awardsCount, peopleCount, aauCount }) {
  const { params, setParams, reset, activeFacetCount } = useAwardSearch();
  const { isUta } = useThemeMode();
  // On phones the stacked controls eat the screen, so collapse them behind a
  // compact bar (the tab toggle stays visible). Default collapsed → results win.
  const isMobile = useMediaQuery("(max-width:600px)");
  const [expanded, setExpanded] = useState(false);

  const isAwards = params.type !== "awardees";

  const isActive =
    params.q ||
    params.fn ||
    params.ln ||
    params.aau ||
    params.uta ||
    activeFacetCount > 0;

  const modeToggle = (
    <ToggleButtonGroup
      className="awards-mode-toggle"
      size="small"
      exclusive
      fullWidth={isMobile}
      value={isAwards ? "awards" : "awardees"}
      onChange={(_, v) =>
        v &&
        setParams({
          type: v,
          page: 1,
          // facets are tab-specific; clear them when switching tabs
          cat: "",
          level: "",
          kind: "",
          org: "",
          acad: "",
          year: "",
          // sort columns and the honorific/UTA quick-filters are tab-specific too
          sort: "",
          dir: "",
          aau: false,
          uta: false,
        })
      }
    >
      <ToggleButton value="awards">
        Awards{awardsCount != null ? ` · ${fmt(awardsCount)}` : ""}
      </ToggleButton>
      <ToggleButton value="awardees">
        Awardees{peopleCount != null ? ` · ${fmt(peopleCount)}` : ""}
      </ToggleButton>
    </ToggleButtonGroup>
  );

  // Awards: one name box. Awardees: separate first- and last-name boxes, which
  // the backend ANDs (both must match when both are filled).
  const searchFields = isAwards ? (
    <DebouncedField paramKey="q" icon placeholder="Search awards by name…" />
  ) : (
    <>
      <DebouncedField
        paramKey="fn"
        className="awards-name-field"
        placeholder="First name"
      />
      <DebouncedField
        paramKey="ln"
        className="awards-name-field"
        placeholder="Last name"
      />
    </>
  );

  // Awardees has only two facets (Academy, Active in year), so they live inline
  // in the main controls instead of behind the Advanced panel - no extra tap on
  // mobile. Awards keeps its four facets in the Advanced panel below.
  const facetFields = !isAwards && (
    <>
      <FacetSelect facet="acad" options={ACADEMY_OPTIONS} className="awards-facet" />
      <FacetSelect facet="year" options={YEAR_OPTIONS} className="awards-facet" />
    </>
  );

  const honorificBtn = isAwards && (
    <Button
      className="awards-honorific-toggle"
      size="small"
      sx={{ borderRadius: "4px" }}
      variant={params.aau ? "contained" : "outlined"}
      color="secondary"
      startIcon={<WorkspacePremiumIcon />}
      onClick={() => setParams({ aau: !params.aau, page: 1 })}
    >
      Honorific awards
      {params.aau && aauCount != null ? ` · ${fmt(aauCount)}` : ""}
    </Button>
  );

  // UTA-network only. Awards tab: awards won by UTA faculty (combines with Honorific). Awardees tab: UTA faculty only.
  const utaBtn = isUta && (
    <Button
      className="awards-uta-toggle"
      size="small"
      sx={{ borderRadius: "4px" }}
      variant={params.uta ? "contained" : "outlined"}
      color="primary"
      startIcon={
        <img src={UTA_MAVERICK} alt="" style={{ height: 18, width: "auto" }} />
      }
      onClick={() => setParams({ uta: !params.uta, page: 1 })}
    >
      {isAwards ? "UTA awards" : "UTA faculty"}
    </Button>
  );

  // Awards only: the Awardees facets are inline now, so it has no Advanced panel.
  const filtersBtn = isAwards && (
    <Button
      className="awards-advanced-toggle"
      size="small"
      variant={params.adv ? "contained" : "outlined"}
      color="primary"
      startIcon={<TuneIcon />}
      onClick={() => setParams({ adv: !params.adv })}
    >
      Filters{activeFacetCount ? ` (${activeFacetCount})` : ""}
    </Button>
  );

  const clearBtn = isActive && (
    <Button variant="text" color="primary" onClick={() => reset()}>
      Clear
    </Button>
  );

  // The full control set, stacked into a column on mobile (via the SCSS).
  const controls = (
    <div className="awards-search-row">
      {!isMobile && modeToggle}
      {searchFields}
      {facetFields}
      {honorificBtn}
      {utaBtn}
      {filtersBtn}
      {clearBtn}
    </div>
  );

  return (
    <div className="awards-search-shell">
      {isMobile ? (
        <>
          <div className="awards-search-mobilebar">
            {modeToggle}
            <Button
              className="awards-mobile-expand"
              size="small"
              fullWidth
              variant={isActive ? "contained" : "outlined"}
              color="primary"
              startIcon={<SearchIcon />}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Hide search & filters" : "Search & filters"}
            </Button>
          </div>
          <Collapse in={expanded} unmountOnExit>
            {controls}
          </Collapse>
        </>
      ) : (
        controls
      )}

      <Collapse in={isAwards && params.adv && (!isMobile || expanded)} unmountOnExit>
        <div className="awards-advanced-panel">
          <AdvancedFilters />
        </div>
      </Collapse>
    </div>
  );
}
